import React, { useState } from "react";
import toast from "react-hot-toast";
import { useSubmitCommentMutation } from "../../redux/api/blogsApi";

// CHANGED: The whole form is now "avatar + bare textarea" — no submit button
// visible until the user clicks into the textarea (focus state).
// This matches Substack's comment input exactly.
//
// Props added:
//   userAvatar — passed down from BlogDetailsPage so we can show the real user photo
//   userName   — used as fallback for the ui-avatars generated initials avatar

const NewComment = ({ blogId, userAvatar, userName }) => {
  const [blogcomment, setBlogComment] = useState("");
  const [focused, setFocused] = useState(false);
  const [submitComment, { isLoading }] = useSubmitCommentMutation();

  const handleSubmit = async () => {
    if (!blogcomment.trim()) return toast.error("Please write a comment");
    try {
      const result = await submitComment({ blogcomment, blogId }).unwrap();
      toast.success(result.updated ? "Comment updated!" : "Comment submitted!");
      setBlogComment("");
      setFocused(false);
    } catch (error) {
      toast.error(error?.data?.message || "Failed to submit comment");
    }
  };

  // CHANGED: pressing Escape cancels the focused state and clears the textarea
  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      setBlogComment("");
      setFocused(false);
    }
  };

  // Fallback to generated avatar using initials if no photo uploaded
  const avatarSrc =
    userAvatar ||
    `https://ui-avatars.com/api/?name=${encodeURIComponent(
      userName || "User"
    )}&background=e7e5e4&color=78716c&size=80`;

  return (
    // CHANGED: Two-column flex layout: avatar on the left, input on the right.
    // This mirrors how Substack renders the comment input.
    <div className="flex gap-3 mb-6">
      {/* User's own avatar — same size as comment avatars for visual consistency */}
      <img
        src={avatarSrc}
        alt={userName || "You"}
        className="w-10 h-10 rounded-full object-cover shrink-0 mt-1"
      />

      {/* Input area */}
      <div className="flex-1">
        {/* CHANGED: Textarea expands from 1 row to 4 rows on focus.
            Rounded-xl and soft border matches Substack's pill-shaped input. */}
        <textarea
          value={blogcomment}
          onChange={(e) => setBlogComment(e.target.value.slice(0, 300))}
          onFocus={() => setFocused(true)}
          onKeyDown={handleKeyDown}
          rows={focused ? 4 : 1}
          placeholder="Write a comment..."
          className="w-full resize-none border border-stone-200 rounded-xl px-4 py-3 text-sm text-stone-800 placeholder:text-stone-400 focus:outline-none focus:border-stone-400 font-sourcesans transition-all duration-200"
        />

        {/* CHANGED: Action row only appears when focused.
            Character counter on the left, Post button on the right.
            Button is a rounded-full pill — same style as reply buttons. */}
        {focused && (
          <div className="flex items-center justify-between mt-2">
            <span className="text-xs text-stone-400 font-sourcesans">
              {blogcomment.length}/300
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setBlogComment("");
                  setFocused(false);
                }}
                className="text-xs text-stone-400 hover:text-stone-600 transition font-sourcesans"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={isLoading || !blogcomment.trim()}
                className="text-xs bg-[#BC3F4A] hover:bg-[#a3323b] disabled:opacity-40 disabled:cursor-not-allowed text-white px-4 py-1.5 rounded-full transition font-sourcesans"
              >
                {isLoading ? "Posting..." : "Post"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NewComment;
